import { Button, Card, Input, Label } from "@aws-amplify/ui-react"
import {
  MachineErrorCodes,
  MachineSerialNumberMaxLength,
} from "@strtmn/sds/schemas"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useContext } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { linkMachineToOwnOrg } from "../../api/MachinesApi"
import { UserDataContext } from "../../contexts"
import { useCurrentTerms } from "../../hooks"
import { DataActConsent } from "../eu-data-act/DataActConsent"
import { LoadingErrorState } from "../LoadingErrorState"

type FormValues = {
  vin: string
  shareWithDealer: boolean
  iHaveReadTheTerms: boolean
}

/** Shows the server's message; the API wraps it as `{ body: { error | message } }`. */
const toErrorMessage = (error: unknown) => {
  const body = (error as any)?.body
  return (
    body?.error ??
    body?.message ??
    (typeof body === "string" ? body : getErrorMessage(error))
  )
}

/**
 * Self-service: links a machine to the org by its VIN, and accepts the EU
 * Data Act terms for it. Customer support is informed by the backend.
 */
interface LinkMachineFormProps {
  orgId: string
  /**
   * Called after the machine was linked, and after the user data was reloaded.
   * @param vin the machine's VIN, upper case as stored
   */
  onLinked?: (vin: string) => void
}

export default function LinkMachineForm({
  orgId,
  onLinked,
}: LinkMachineFormProps) {
  const { reloadUserDataAsync } = useContext(UserDataContext)
  const terms = useCurrentTerms()
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    // `false` while the dealer question is hidden outside dev: nobody is
    // asked, so nothing is consented to. Flip when the checkbox is shown.
    defaultValues: {
      vin: "",
      shareWithDealer: false,
      iHaveReadTheTerms: false,
    },
  })

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    // VINs are stored upper case, and the backend matches case-insensitively,
    // so this is the canonical form — which `onLinked` can link to.
    const vin = data.vin.trim().toUpperCase()
    try {
      await linkMachineToOwnOrg(orgId, {
        machineSerialNumber: vin,
        termsVersion: terms.termsVersion!,
        shareWithDealer: data.shareWithDealer,
      })
      // Shows the machine in the org
      await reloadUserDataAsync()
      onLinked?.(vin)
    } catch (error) {
      if ((error as any)?.body?.code === MachineErrorCodes.AlreadyInOrg) {
        // Already linked, so the goal is reached either way
        await reloadUserDataAsync()
        onLinked?.(vin)
        return
      }
      setError("root.serverError", { message: toErrorMessage(error) })
    }
  }

  if (terms.isLoading || terms.error || !terms.termsVersion) {
    return (
      <LoadingErrorState
        isLoading={terms.isLoading}
        loadingMessage={I18n.get("Lade Daten ...")}
        isError={!terms.isLoading}
        errorMessage={`${I18n.get("Fehler")}: ${terms.error ?? I18n.get("Datennutzungsvereinbarung nicht gefunden.")}`}
      />
    )
  }

  const requiredMessage = I18n.get("Geben Sie die FIN der Maschine an.")

  return (
    <Card>
      <h3>{I18n.get("Maschine verknüpfen")}</h3>
      <p className="mb-2">
        {I18n.get(
          "Verknüpfen Sie Ihre Maschine mit Ihrem Betrieb, um ihre Daten hier zu sehen."
        )}
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <Label htmlFor="link-machine-vin">
            {I18n.get("FIN der Maschine")}
          </Label>
          <Input
            id="link-machine-vin"
            type="text"
            size="small"
            placeholder={I18n.get("FIN")}
            autoComplete="off"
            {...register("vin", {
              required: requiredMessage,
              validate: (value) => value.trim().length > 0 || requiredMessage,
              maxLength: {
                value: MachineSerialNumberMaxLength,
                message: `Max. ${MachineSerialNumberMaxLength} Zeichen.`,
              },
            })}
            className={errors.vin ? "error" : ""}
          />
          {errors.vin && <div className="error">{errors.vin.message}</div>}
        </div>

        <DataActConsent
          currentTermsVersion={terms.termsVersion}
          currentTermsUrl={terms.termsUrl!}
          shareWithDealer={register("shareWithDealer")}
          acknowledge={register("iHaveReadTheTerms")}
        />

        {errors.root?.["serverError"] && (
          <p className="error">{errors.root["serverError"].message}</p>
        )}

        <Button
          type="submit"
          variation="primary"
          size="small"
          isDisabled={!watch("iHaveReadTheTerms") || isSubmitting}
          isLoading={isSubmitting}
          loadingText={I18n.get("Wird gespeichert...")}
        >
          {I18n.get(
            "Maschine verknüpfen und der Datennutzungsvereinbarung zustimmen"
          )}
        </Button>
      </form>
    </Card>
  )
}
